import type { LangMap } from "./index";

const en = {
  title: "Holidays, catch-up & exams",
  subtitle: "Spread the work out instead of piling it up at the end",
  hub: {
    summer: "Summer holidays",
    summerDesc: "Organise your summer without turning it into a second school year.",
    recovery: "Catch-up / resits",
    recoveryDesc: "A failing term, a resit, a topic you never got. All year round.",
    exam: "Exam preparation",
    examDesc: "School exams, university exams, language certificates, entrance tests.",
    active: "Your plans",
    empty: "No plan yet. Pick one above and Lumen will build it with you.",
    open: "Open",
  },
  wizard: {
    summerIntro: "Let's organise your summer without turning it into a second school year.",
    debtsQ: "Do you have subjects to catch up on or resits?",
    yes: "Yes",
    no: "No",
    unknown: "Not yet sure",
    debtsHint: "We'll add a Catch-up plan next to your summer plan.",
    period: "Your holiday window",
    lastDay: "Last day of school",
    firstDay: "First day of the new school year",
    dailyMax: "Maximum time per day",
    restDays: "Days you'd rather not study",
    travel: "Travelling / away (optional)",
    addTravel: "Add a trip",
    from: "From",
    to: "To",
    ageBand: "School level",
    primary: "Primary",
    middle: "Middle school",
    high: "High school",
    university: "University",
    subject: "Subject",
    subjectPh: "e.g. Maths",
    scopeQ: "What do you need to catch up on?",
    scope: {
      topic: "One topic",
      topics: "Several topics",
      term1: "First term",
      term2: "Second term",
      year: "The whole year",
      unknown: "I don't know",
    },
    unknownHint: "No problem: Lumen will work it out from your program, past tests and the material you upload.",
    testDateQ: "When is your test?",
    noTestDate: "I don't know the date yet",
    noTestDateHint: "We'll start working anyway and update the plan as soon as you have the date.",
    examName: "What are you preparing for?",
    examNamePh: "e.g. Analysis 1, B2 First, entrance test",
    goal: "Your goal (optional)",
    goalPh: "e.g. pass with a solid grade",
    create: "Create the plan",
    creating: "Creating…",
    next: "Continue",
    back: "Back",
  },
  items: {
    title: "Your work",
    add: "Add work",
    text: "Write it",
    textPh: "Maths: exercises 1-120\nEnglish: units 1-6\nRead The Baron in the Trees\nSummary of chapter 4",
    photo: "Photo / file",
    photoHint: "Photograph the homework sheet: Lumen reads subjects, pages, exercises and deadlines.",
    parse: "Read it with AI",
    parsing: "Reading…",
    confirmTitle: "Check before saving",
    confirmHint: "Edit anything that's wrong, then save.",
    save: "Save",
    empty: "Nothing added yet.",
    left: "{{left}} of {{total}} {{unit}} left",
    book: "Book to read",
    bookTitle: "Title",
    author: "Author",
    pages: "Pages",
    currentPage: "Current page",
    finishBy: "Finish by",
    finalTask: "Final task",
    finalTasks: {
      none: "None",
      summary: "Summary",
      sheet: "Book sheet",
      presentation: "Presentation",
      report: "Report",
      questions: "Questions",
    },
    addBook: "Add book",
    perDay: "About {{n}} {{unit}} a day is enough.",
    difficulty: "Difficulty",
    remove: "Remove",
  },
  plan: {
    build: "Create my plan",
    rebuild: "Rebuild the plan",
    building: "Lumen is spreading out the work…",
    summary: "Your plan",
    phases: "Phases",
    today: "Today",
    todayEmpty: "Nothing planned today. Enjoy it.",
    totalToday: "Total today: {{min}} min",
    done: "Done",
    skip: "Skip",
    start: "Start",
    upcoming: "Coming up",
    behind: "I've fallen behind",
    behindHint: "Lumen re-plans what's left, with priorities. No lectures.",
    reschedule: "Reorganise the plan",
    rescheduleHint: "Missed a day? The work is spread over several days, never dumped on tomorrow.",
    ask: "Ask Lumen",
    askPh: "e.g. I've done nothing and it's 10 August",
    send: "Send",
    noDays: "Set the dates of the period first.",
    backToSchool: "Back to School",
    progress: "Progress",
    readiness: "Readiness",
    daysLeft: "{{n}} days left",
    testOn: "Test on {{date}}",
    onlyMinutes: "I only have {{n}} minutes",
    priority: { high: "High priority", medium: "Medium", low: "Low" },
    status: { recovering: "Getting there", almost: "Almost ready", ready: "Ready", weak: "Needs work" },
  },
  study: {
    diagnostic: "Let's see where you stand",
    diagnosticHint: "A short test that doesn't count for grades: it finds your gaps.",
    startDiagnostic: "Start the test",
    exercises: "Exercises",
    exercisesHint: "Real exercises created for you, with full correction.",
    generate: "Generate exercises",
    level: { base: "Base", intermediate: "Intermediate", advanced: "Advanced", exam: "Exam simulation" },
    topicPh: "Topic (e.g. quadratic equations)",
    yourAnswer: "Your answer",
    yourAnswerPh: "Write your steps, or attach a photo of your work",
    correct: "Correct it",
    correcting: "Correcting…",
    verdict: { correct: "Correct", partly: "Almost", wrong: "Not yet" },
    whereWrong: "Where it went wrong",
    why: "Why",
    howToFix: "How to fix it",
    rule: "Rule to remember",
    similar: "Try a similar one",
    errorLog: "My mistakes",
    errorLogEmpty: "No mistakes recorded yet.",
    times: "{{n}}x",
    simulate: "Mock test",
    simulateHint: "A real test under time pressure. Solutions only at the end.",
    duration: "Duration",
    custom: "Custom",
    startSim: "Start the mock test",
    showSolutions: "Show solutions",
    solution: "Solution",
    points: "{{n}} pts",
  },
  reminders: {
    title: "Daily reminder",
    hint: "A light nudge each day, never a guilt trip.",
  },
  errors: {
    generic: "Something went wrong. Try again.",
  },
};

const it: typeof en = {
  title: "Vacanze, recupero ed esami",
  subtitle: "Distribuisci il lavoro invece di ammassarlo alla fine",
  hub: {
    summer: "Vacanze estive",
    summerDesc: "Organizziamo la tua estate senza trasformarla in un secondo anno scolastico.",
    recovery: "Recupero / Debiti",
    recoveryDesc: "Insufficienza, debito, prova di recupero o un argomento mai capito. Tutto l'anno.",
    exam: "Preparazione esami",
    examDesc: "Esami scolastici e universitari, certificazioni linguistiche, test di ingresso.",
    active: "I tuoi piani",
    empty: "Nessun piano attivo. Scegline uno qui sopra e lo costruiamo insieme.",
    open: "Apri",
  },
  wizard: {
    summerIntro: "Organizziamo la tua estate senza trasformarla in un secondo anno scolastico.",
    debtsQ: "Hai materie da recuperare o debiti?",
    yes: "Sì",
    no: "No",
    unknown: "Non lo so ancora",
    debtsHint: "Aggiungeremo un piano di Recupero accanto a quello estivo.",
    period: "Il periodo delle vacanze",
    lastDay: "Ultimo giorno di scuola",
    firstDay: "Primo giorno del nuovo anno",
    dailyMax: "Tempo massimo al giorno",
    restDays: "Giorni in cui preferisci non studiare",
    travel: "Periodi in viaggio (facoltativo)",
    addTravel: "Aggiungi un viaggio",
    from: "Dal",
    to: "Al",
    ageBand: "Livello scolastico",
    primary: "Primaria",
    middle: "Medie",
    high: "Superiori",
    university: "Università",
    subject: "Materia",
    subjectPh: "es. Matematica",
    scopeQ: "Che cosa devi recuperare?",
    scope: {
      topic: "Singolo argomento",
      topics: "Più argomenti",
      term1: "Trimestre",
      term2: "Pentamestre",
      year: "Tutto l'anno",
      unknown: "Non lo so",
    },
    unknownHint: "Nessun problema: Lumen lo capisce dal programma, dalle verifiche precedenti e dal materiale che carichi.",
    testDateQ: "Quando hai la prova di recupero?",
    noTestDate: "Non conosco ancora la data",
    noTestDateHint: "Lavoriamo comunque sul recupero e aggiorneremo il piano quando avrai la data.",
    examName: "Per cosa ti stai preparando?",
    examNamePh: "es. Analisi 1, B2 First, test d'ingresso",
    goal: "Il tuo obiettivo (facoltativo)",
    goalPh: "es. passare con un voto solido",
    create: "Crea il piano",
    creating: "Creo il piano…",
    next: "Avanti",
    back: "Indietro",
  },
  items: {
    title: "I tuoi compiti",
    add: "Aggiungi compiti",
    text: "Scrivi",
    textPh: "Matematica: esercizi 1-120\nInglese: unità 1-6\nLeggere Il barone rampante\nRiassunto del capitolo 4",
    photo: "Foto / file",
    photoHint: "Fotografa il foglio dei compiti: Lumen riconosce materie, pagine, esercizi e scadenze.",
    parse: "Leggi con l'AI",
    parsing: "Sto leggendo…",
    confirmTitle: "Controlla prima di salvare",
    confirmHint: "Modifica quello che non torna, poi salva.",
    save: "Salva",
    empty: "Non hai ancora aggiunto nulla.",
    left: "{{left}} di {{total}} {{unit}} rimaste",
    book: "Libro da leggere",
    bookTitle: "Titolo",
    author: "Autore",
    pages: "Pagine",
    currentPage: "Pagina attuale",
    finishBy: "Da finire entro",
    finalTask: "Attività finale",
    finalTasks: {
      none: "Nessuna",
      summary: "Riassunto",
      sheet: "Scheda libro",
      presentation: "Presentazione",
      report: "Relazione",
      questions: "Domande",
    },
    addBook: "Aggiungi libro",
    perDay: "Ti bastano circa {{n}} {{unit}} al giorno.",
    difficulty: "Difficoltà",
    remove: "Elimina",
  },
  plan: {
    build: "Crea il mio piano",
    rebuild: "Rigenera il piano",
    building: "Sto distribuendo il lavoro…",
    summary: "Il tuo piano",
    phases: "Fasi",
    today: "Oggi",
    todayEmpty: "Oggi non c'è nulla in programma. Goditela.",
    totalToday: "Totale oggi: {{min}} min",
    done: "Fatto",
    skip: "Salta",
    start: "Inizia",
    upcoming: "Prossimi giorni",
    behind: "Sono rimasto indietro",
    behindHint: "Lumen ricalcola il piano con le priorità giuste. Senza prediche.",
    reschedule: "Riorganizza il piano",
    rescheduleHint: "Saltata una giornata? Il lavoro viene distribuito su più giorni, mai tutto sul giorno dopo.",
    ask: "Chiedi a Lumen",
    askPh: "es. Ho fatto zero compiti e siamo al 10 agosto",
    send: "Invia",
    noDays: "Imposta prima le date del periodo.",
    backToSchool: "Back to School",
    progress: "Avanzamento",
    readiness: "Preparazione",
    daysLeft: "Mancano {{n}} giorni",
    testOn: "Prova il {{date}}",
    onlyMinutes: "Ho solo {{n}} minuti",
    priority: { high: "Priorità alta", medium: "Media", low: "Bassa" },
    status: { recovering: "In recupero", almost: "Quasi pronto", ready: "Pronto", weak: "Da rinforzare" },
  },
  study: {
    diagnostic: "Vediamo da dove partiamo",
    diagnosticHint: "Un test breve che non incide sui voti: serve a trovare le lacune.",
    startDiagnostic: "Inizia il test",
    exercises: "Esercizi",
    exercisesHint: "Esercizi veri creati per te, con correzione completa.",
    generate: "Genera esercizi",
    level: { base: "Base", intermediate: "Intermedio", advanced: "Avanzato", exam: "Simulazione prova" },
    topicPh: "Argomento (es. equazioni di secondo grado)",
    yourAnswer: "La tua risposta",
    yourAnswerPh: "Scrivi i passaggi, oppure allega una foto del tuo foglio",
    correct: "Correggi",
    correcting: "Sto correggendo…",
    verdict: { correct: "Corretto", partly: "Quasi", wrong: "Non ancora" },
    whereWrong: "Dove hai sbagliato",
    why: "Perché",
    howToFix: "Come correggerlo",
    rule: "Regola da ricordare",
    similar: "Prova un esercizio simile",
    errorLog: "I miei errori",
    errorLogEmpty: "Nessun errore registrato per ora.",
    times: "{{n}} errori",
    simulate: "Simula esame",
    simulateHint: "Una prova vera a tempo. Le soluzioni solo alla fine.",
    duration: "Durata",
    custom: "Personalizzata",
    startSim: "Inizia la simulazione",
    showSolutions: "Mostra le soluzioni",
    solution: "Soluzione",
    points: "{{n}} pt",
  },
  reminders: {
    title: "Promemoria quotidiano",
    hint: "Una spinta leggera ogni giorno, mai un rimprovero.",
  },
  errors: {
    generic: "Qualcosa non ha funzionato. Riprova.",
  },
};

const es: typeof en = {
  title: "Vacaciones, recuperación y exámenes",
  subtitle: "Reparte el trabajo en vez de dejarlo todo para el final",
  hub: {
    summer: "Vacaciones de verano",
    summerDesc: "Organiza tu verano sin convertirlo en un segundo año escolar.",
    recovery: "Recuperación / asignaturas pendientes",
    recoveryDesc: "Una evaluación suspendida, una recuperación, un tema que nunca entendiste. Todo el año.",
    exam: "Preparación de exámenes",
    examDesc: "Exámenes escolares, universitarios, certificados de idiomas, pruebas de acceso.",
    active: "Tus planes",
    empty: "Todavía no tienes ningún plan. Elige uno arriba y Lumen lo construirá contigo.",
    open: "Abrir",
  },
  wizard: {
    summerIntro: "Organicemos tu verano sin convertirlo en un segundo año escolar.",
    debtsQ: "¿Tienes asignaturas pendientes o recuperaciones?",
    yes: "Sí",
    no: "No",
    unknown: "Todavía no lo sé",
    debtsHint: "Añadiremos un plan de Recuperación junto a tu plan de verano.",
    period: "Tu periodo de vacaciones",
    lastDay: "Último día de clase",
    firstDay: "Primer día del nuevo curso",
    dailyMax: "Tiempo máximo al día",
    restDays: "Días en los que prefieres no estudiar",
    travel: "Viajes / ausencias (opcional)",
    addTravel: "Añadir un viaje",
    from: "Desde",
    to: "Hasta",
    ageBand: "Nivel escolar",
    primary: "Primaria",
    middle: "Secundaria (ESO)",
    high: "Bachillerato",
    university: "Universidad",
    subject: "Asignatura",
    subjectPh: "p. ej. Matemáticas",
    scopeQ: "¿Qué necesitas recuperar?",
    scope: {
      topic: "Un tema",
      topics: "Varios temas",
      term1: "Primer trimestre",
      term2: "Segundo trimestre",
      year: "Todo el año",
      unknown: "No lo sé",
    },
    unknownHint: "No pasa nada: Lumen lo deducirá de tu programa, tus exámenes anteriores y el material que subas.",
    testDateQ: "¿Cuándo es tu prueba?",
    noTestDate: "Todavía no sé la fecha",
    noTestDateHint: "Empezaremos a trabajar igualmente y actualizaremos el plan en cuanto tengas la fecha.",
    examName: "¿Para qué te estás preparando?",
    examNamePh: "p. ej. Análisis 1, B2 First, prueba de acceso",
    goal: "Tu objetivo (opcional)",
    goalPh: "p. ej. aprobar con una nota sólida",
    create: "Crear el plan",
    creating: "Creando…",
    next: "Continuar",
    back: "Atrás",
  },
  items: {
    title: "Tu trabajo",
    add: "Añadir trabajo",
    text: "Escríbelo",
    textPh: "Matemáticas: ejercicios 1-120\nInglés: unidades 1-6\nLeer El barón rampante\nResumen del capítulo 4",
    photo: "Foto / archivo",
    photoHint: "Fotografía la hoja de tareas: Lumen reconoce asignaturas, páginas, ejercicios y plazos.",
    parse: "Léelo con IA",
    parsing: "Leyendo…",
    confirmTitle: "Revisa antes de guardar",
    confirmHint: "Corrige lo que esté mal y luego guarda.",
    save: "Guardar",
    empty: "Todavía no has añadido nada.",
    left: "Quedan {{left}} de {{total}} {{unit}}",
    book: "Libro para leer",
    bookTitle: "Título",
    author: "Autor",
    pages: "Páginas",
    currentPage: "Página actual",
    finishBy: "Terminar antes de",
    finalTask: "Tarea final",
    finalTasks: {
      none: "Ninguna",
      summary: "Resumen",
      sheet: "Ficha de lectura",
      presentation: "Presentación",
      report: "Informe",
      questions: "Preguntas",
    },
    addBook: "Añadir libro",
    perDay: "Con unas {{n}} {{unit}} al día es suficiente.",
    difficulty: "Dificultad",
    remove: "Eliminar",
  },
  plan: {
    build: "Crear mi plan",
    rebuild: "Rehacer el plan",
    building: "Lumen está repartiendo el trabajo…",
    summary: "Tu plan",
    phases: "Fases",
    today: "Hoy",
    todayEmpty: "Hoy no hay nada planeado. Disfrútalo.",
    totalToday: "Total de hoy: {{min}} min",
    done: "Hecho",
    skip: "Saltar",
    start: "Empezar",
    upcoming: "Próximamente",
    behind: "Me he atrasado",
    behindHint: "Lumen replanifica lo que falta, con prioridades. Sin sermones.",
    reschedule: "Reorganizar el plan",
    rescheduleHint: "¿Te has saltado un día? El trabajo se reparte entre varios días, nunca se acumula en el siguiente.",
    ask: "Preguntar a Lumen",
    askPh: "p. ej. No he hecho nada y ya es 10 de agosto",
    send: "Enviar",
    noDays: "Primero define las fechas del periodo.",
    backToSchool: "Vuelta al cole",
    progress: "Progreso",
    readiness: "Preparación",
    daysLeft: "Quedan {{n}} días",
    testOn: "Prueba el {{date}}",
    onlyMinutes: "Solo tengo {{n}} minutos",
    priority: { high: "Prioridad alta", medium: "Media", low: "Baja" },
    status: { recovering: "En recuperación", almost: "Casi listo", ready: "Listo", weak: "Necesita refuerzo" },
  },
  study: {
    diagnostic: "Veamos dónde estás",
    diagnosticHint: "Un test breve que no cuenta para la nota: sirve para encontrar tus lagunas.",
    startDiagnostic: "Empezar el test",
    exercises: "Ejercicios",
    exercisesHint: "Ejercicios reales creados para ti, con corrección completa.",
    generate: "Generar ejercicios",
    level: { base: "Básico", intermediate: "Intermedio", advanced: "Avanzado", exam: "Simulación de examen" },
    topicPh: "Tema (p. ej. ecuaciones de segundo grado)",
    yourAnswer: "Tu respuesta",
    yourAnswerPh: "Escribe tus pasos, o adjunta una foto de tu trabajo",
    correct: "Corregir",
    correcting: "Corrigiendo…",
    verdict: { correct: "Correcto", partly: "Casi", wrong: "Todavía no" },
    whereWrong: "Dónde se ha fallado",
    why: "Por qué",
    howToFix: "Cómo corregirlo",
    rule: "Regla que recordar",
    similar: "Probar uno parecido",
    errorLog: "Mis errores",
    errorLogEmpty: "Todavía no hay errores registrados.",
    times: "{{n}}x",
    simulate: "Examen simulado",
    simulateHint: "Una prueba real con tiempo limitado. Soluciones solo al final.",
    duration: "Duración",
    custom: "Personalizado",
    startSim: "Empezar el examen simulado",
    showSolutions: "Mostrar soluciones",
    solution: "Solución",
    points: "{{n}} pts",
  },
  reminders: {
    title: "Recordatorio diario",
    hint: "Un empujón suave cada día, nunca un reproche.",
  },
  errors: {
    generic: "Algo ha ido mal. Inténtalo de nuevo.",
  },
};

const fr: typeof en = {
  title: "Vacances, rattrapage et examens",
  subtitle: "Répartis le travail au lieu de tout entasser à la fin",
  hub: {
    summer: "Vacances d'été",
    summerDesc: "Organise ton été sans en faire une deuxième année scolaire.",
    recovery: "Rattrapage / matières en retard",
    recoveryDesc: "Un trimestre raté, un rattrapage, une notion jamais comprise. Toute l'année.",
    exam: "Préparation aux examens",
    examDesc: "Examens scolaires, examens universitaires, certifications de langues, concours d'entrée.",
    active: "Tes plans",
    empty: "Aucun plan pour l'instant. Choisis-en un ci-dessus et Lumen le construira avec toi.",
    open: "Ouvrir",
  },
  wizard: {
    summerIntro: "Organisons ton été sans en faire une deuxième année scolaire.",
    debtsQ: "As-tu des matières à rattraper ou des rattrapages ?",
    yes: "Oui",
    no: "Non",
    unknown: "Pas encore sûr",
    debtsHint: "Nous ajouterons un plan de Rattrapage à côté de ton plan d'été.",
    period: "Ta période de vacances",
    lastDay: "Dernier jour d'école",
    firstDay: "Premier jour de la rentrée",
    dailyMax: "Temps maximum par jour",
    restDays: "Jours où tu préfères ne pas étudier",
    travel: "Voyages / absences (facultatif)",
    addTravel: "Ajouter un voyage",
    from: "Du",
    to: "Au",
    ageBand: "Niveau scolaire",
    primary: "Primaire",
    middle: "Collège",
    high: "Lycée",
    university: "Université",
    subject: "Matière",
    subjectPh: "ex. Mathématiques",
    scopeQ: "Qu'as-tu besoin de rattraper ?",
    scope: {
      topic: "Un sujet",
      topics: "Plusieurs sujets",
      term1: "Premier trimestre",
      term2: "Deuxième trimestre",
      year: "Toute l'année",
      unknown: "Je ne sais pas",
    },
    unknownHint: "Aucun problème : Lumen le déduira de ton programme, de tes anciens contrôles et des documents que tu envoies.",
    testDateQ: "Quand as-tu ton contrôle ?",
    noTestDate: "Je ne connais pas encore la date",
    noTestDateHint: "Nous commencerons quand même à travailler et mettrons le plan à jour dès que tu auras la date.",
    examName: "Pour quoi te prépares-tu ?",
    examNamePh: "ex. Analyse 1, B2 First, concours d'entrée",
    goal: "Ton objectif (facultatif)",
    goalPh: "ex. réussir avec une bonne note",
    create: "Créer le plan",
    creating: "Création en cours…",
    next: "Continuer",
    back: "Retour",
  },
  items: {
    title: "Ton travail",
    add: "Ajouter du travail",
    text: "Écris-le",
    textPh: "Maths : exercices 1-120\nAnglais : unités 1-6\nLire Le Baron perché\nRésumé du chapitre 4",
    photo: "Photo / fichier",
    photoHint: "Photographie la feuille de devoirs : Lumen reconnaît les matières, pages, exercices et échéances.",
    parse: "Lire avec l'IA",
    parsing: "Lecture en cours…",
    confirmTitle: "Vérifie avant d'enregistrer",
    confirmHint: "Corrige ce qui ne va pas, puis enregistre.",
    save: "Enregistrer",
    empty: "Rien n'a encore été ajouté.",
    left: "{{left}} sur {{total}} {{unit}} restants",
    book: "Livre à lire",
    bookTitle: "Titre",
    author: "Auteur",
    pages: "Pages",
    currentPage: "Page actuelle",
    finishBy: "À terminer avant le",
    finalTask: "Tâche finale",
    finalTasks: {
      none: "Aucune",
      summary: "Résumé",
      sheet: "Fiche de lecture",
      presentation: "Présentation",
      report: "Rapport",
      questions: "Questions",
    },
    addBook: "Ajouter un livre",
    perDay: "Environ {{n}} {{unit}} par jour suffisent.",
    difficulty: "Difficulté",
    remove: "Supprimer",
  },
  plan: {
    build: "Créer mon plan",
    rebuild: "Refaire le plan",
    building: "Lumen répartit le travail…",
    summary: "Ton plan",
    phases: "Phases",
    today: "Aujourd'hui",
    todayEmpty: "Rien de prévu aujourd'hui. Profites-en.",
    totalToday: "Total aujourd'hui : {{min}} min",
    done: "Fait",
    skip: "Passer",
    start: "Commencer",
    upcoming: "À venir",
    behind: "J'ai pris du retard",
    behindHint: "Lumen replanifie ce qu'il reste, avec des priorités. Sans reproches.",
    reschedule: "Réorganiser le plan",
    rescheduleHint: "Une journée sautée ? Le travail est réparti sur plusieurs jours, jamais entassé le lendemain.",
    ask: "Demander à Lumen",
    askPh: "ex. Je n'ai rien fait et on est déjà le 10 août",
    send: "Envoyer",
    noDays: "Définis d'abord les dates de la période.",
    backToSchool: "Rentrée scolaire",
    progress: "Progression",
    readiness: "Préparation",
    daysLeft: "Il reste {{n}} jours",
    testOn: "Contrôle le {{date}}",
    onlyMinutes: "Je n'ai que {{n}} minutes",
    priority: { high: "Priorité haute", medium: "Moyenne", low: "Basse" },
    status: { recovering: "En progrès", almost: "Presque prêt", ready: "Prêt", weak: "À renforcer" },
  },
  study: {
    diagnostic: "Voyons où tu en es",
    diagnosticHint: "Un court test qui ne compte pas pour la note : il repère tes lacunes.",
    startDiagnostic: "Commencer le test",
    exercises: "Exercices",
    exercisesHint: "De vrais exercices créés pour toi, avec correction complète.",
    generate: "Générer des exercices",
    level: { base: "Base", intermediate: "Intermédiaire", advanced: "Avancé", exam: "Simulation d'examen" },
    topicPh: "Sujet (ex. équations du second degré)",
    yourAnswer: "Ta réponse",
    yourAnswerPh: "Écris tes étapes, ou joins une photo de ton travail",
    correct: "Corriger",
    correcting: "Correction en cours…",
    verdict: { correct: "Correct", partly: "Presque", wrong: "Pas encore" },
    whereWrong: "Où ça a coincé",
    why: "Pourquoi",
    howToFix: "Comment corriger",
    rule: "Règle à retenir",
    similar: "Essayer un exercice similaire",
    errorLog: "Mes erreurs",
    errorLogEmpty: "Aucune erreur enregistrée pour l'instant.",
    times: "{{n}}x",
    simulate: "Examen blanc",
    simulateHint: "Un vrai contrôle chronométré. Les solutions seulement à la fin.",
    duration: "Durée",
    custom: "Personnalisé",
    startSim: "Commencer l'examen blanc",
    showSolutions: "Afficher les solutions",
    solution: "Solution",
    points: "{{n}} pts",
  },
  reminders: {
    title: "Rappel quotidien",
    hint: "Un petit coup de pouce chaque jour, jamais un reproche.",
  },
  errors: {
    generic: "Une erreur est survenue. Réessaie.",
  },
};

const de: typeof en = {
  title: "Ferien, Nachholen & Prüfungen",
  subtitle: "Verteile die Arbeit, statt sie am Ende aufzutürmen",
  hub: {
    summer: "Sommerferien",
    summerDesc: "Organisiere deinen Sommer, ohne dass er zu einem zweiten Schuljahr wird.",
    recovery: "Nachholen / Nachprüfungen",
    recoveryDesc: "Ein nicht bestandenes Halbjahr, eine Nachprüfung, ein Thema, das du nie verstanden hast. Das ganze Jahr über.",
    exam: "Prüfungsvorbereitung",
    examDesc: "Schulprüfungen, Universitätsprüfungen, Sprachzertifikate, Aufnahmetests.",
    active: "Deine Pläne",
    empty: "Noch kein Plan. Wähle oben einen aus und Lumen erstellt ihn mit dir.",
    open: "Öffnen",
  },
  wizard: {
    summerIntro: "Lass uns deinen Sommer organisieren, ohne dass er zu einem zweiten Schuljahr wird.",
    debtsQ: "Hast du Fächer nachzuholen oder Nachprüfungen?",
    yes: "Ja",
    no: "Nein",
    unknown: "Noch nicht sicher",
    debtsHint: "Wir fügen neben deinem Sommerplan einen Nachhol-Plan hinzu.",
    period: "Dein Ferienzeitraum",
    lastDay: "Letzter Schultag",
    firstDay: "Erster Tag des neuen Schuljahres",
    dailyMax: "Maximale Zeit pro Tag",
    restDays: "Tage, an denen du lieber nicht lernst",
    travel: "Reisen / Abwesenheit (optional)",
    addTravel: "Reise hinzufügen",
    from: "Von",
    to: "Bis",
    ageBand: "Schulstufe",
    primary: "Grundschule",
    middle: "Mittelschule",
    high: "Oberschule",
    university: "Universität",
    subject: "Fach",
    subjectPh: "z. B. Mathematik",
    scopeQ: "Was musst du nachholen?",
    scope: {
      topic: "Ein Thema",
      topics: "Mehrere Themen",
      term1: "Erstes Halbjahr",
      term2: "Zweites Halbjahr",
      year: "Das ganze Jahr",
      unknown: "Ich weiß es nicht",
    },
    unknownHint: "Kein Problem: Lumen ermittelt es aus deinem Lehrplan, früheren Tests und dem hochgeladenen Material.",
    testDateQ: "Wann ist deine Prüfung?",
    noTestDate: "Ich kenne das Datum noch nicht",
    noTestDateHint: "Wir fangen trotzdem an zu arbeiten und aktualisieren den Plan, sobald du das Datum hast.",
    examName: "Worauf bereitest du dich vor?",
    examNamePh: "z. B. Analysis 1, B2 First, Aufnahmetest",
    goal: "Dein Ziel (optional)",
    goalPh: "z. B. mit einer soliden Note bestehen",
    create: "Plan erstellen",
    creating: "Wird erstellt…",
    next: "Weiter",
    back: "Zurück",
  },
  items: {
    title: "Deine Aufgaben",
    add: "Aufgabe hinzufügen",
    text: "Schreib es auf",
    textPh: "Mathe: Aufgaben 1-120\nEnglisch: Einheiten 1-6\nDer Baron auf den Bäumen lesen\nZusammenfassung Kapitel 4",
    photo: "Foto / Datei",
    photoHint: "Fotografiere das Hausaufgabenblatt: Lumen erkennt Fächer, Seiten, Übungen und Fristen.",
    parse: "Mit KI lesen",
    parsing: "Wird gelesen…",
    confirmTitle: "Vor dem Speichern prüfen",
    confirmHint: "Korrigiere alles Falsche und speichere dann.",
    save: "Speichern",
    empty: "Noch nichts hinzugefügt.",
    left: "Noch {{left}} von {{total}} {{unit}}",
    book: "Zu lesendes Buch",
    bookTitle: "Titel",
    author: "Autor",
    pages: "Seiten",
    currentPage: "Aktuelle Seite",
    finishBy: "Fertig bis",
    finalTask: "Abschlussaufgabe",
    finalTasks: {
      none: "Keine",
      summary: "Zusammenfassung",
      sheet: "Lesebogen",
      presentation: "Präsentation",
      report: "Bericht",
      questions: "Fragen",
    },
    addBook: "Buch hinzufügen",
    perDay: "Etwa {{n}} {{unit}} pro Tag reichen aus.",
    difficulty: "Schwierigkeit",
    remove: "Entfernen",
  },
  plan: {
    build: "Meinen Plan erstellen",
    rebuild: "Plan neu erstellen",
    building: "Lumen verteilt die Arbeit…",
    summary: "Dein Plan",
    phases: "Phasen",
    today: "Heute",
    todayEmpty: "Für heute ist nichts geplant. Genieß es.",
    totalToday: "Heute insgesamt: {{min}} Min",
    done: "Erledigt",
    skip: "Überspringen",
    start: "Starten",
    upcoming: "Demnächst",
    behind: "Ich bin im Rückstand",
    behindHint: "Lumen plant das Verbleibende mit Prioritäten neu. Ohne Vorträge.",
    reschedule: "Plan neu organisieren",
    rescheduleHint: "Einen Tag verpasst? Die Arbeit wird über mehrere Tage verteilt, nie alles auf morgen abgewälzt.",
    ask: "Lumen fragen",
    askPh: "z. B. Ich habe nichts gemacht und es ist schon der 10. August",
    send: "Senden",
    noDays: "Lege zuerst die Daten des Zeitraums fest.",
    backToSchool: "Zurück zur Schule",
    progress: "Fortschritt",
    readiness: "Bereitschaft",
    daysLeft: "Noch {{n}} Tage",
    testOn: "Prüfung am {{date}}",
    onlyMinutes: "Ich habe nur {{n}} Minuten",
    priority: { high: "Hohe Priorität", medium: "Mittel", low: "Niedrig" },
    status: { recovering: "Auf dem Weg", almost: "Fast bereit", ready: "Bereit", weak: "Braucht Arbeit" },
  },
  study: {
    diagnostic: "Schauen wir, wo du stehst",
    diagnosticHint: "Ein kurzer Test, der nicht benotet wird: er findet deine Lücken.",
    startDiagnostic: "Test starten",
    exercises: "Übungen",
    exercisesHint: "Echte, für dich erstellte Übungen mit vollständiger Korrektur.",
    generate: "Übungen erstellen",
    level: { base: "Basis", intermediate: "Mittelstufe", advanced: "Fortgeschritten", exam: "Prüfungssimulation" },
    topicPh: "Thema (z. B. quadratische Gleichungen)",
    yourAnswer: "Deine Antwort",
    yourAnswerPh: "Schreibe deine Schritte auf oder füge ein Foto deiner Arbeit bei",
    correct: "Korrigieren",
    correcting: "Wird korrigiert…",
    verdict: { correct: "Richtig", partly: "Fast", wrong: "Noch nicht" },
    whereWrong: "Wo es falsch lief",
    why: "Warum",
    howToFix: "Wie man es korrigiert",
    rule: "Regel zum Merken",
    similar: "Ähnliche Aufgabe versuchen",
    errorLog: "Meine Fehler",
    errorLogEmpty: "Noch keine Fehler erfasst.",
    times: "{{n}}x",
    simulate: "Probeprüfung",
    simulateHint: "Eine echte Prüfung unter Zeitdruck. Lösungen erst am Ende.",
    duration: "Dauer",
    custom: "Benutzerdefiniert",
    startSim: "Probeprüfung starten",
    showSolutions: "Lösungen anzeigen",
    solution: "Lösung",
    points: "{{n}} Pkt.",
  },
  reminders: {
    title: "Tägliche Erinnerung",
    hint: "Ein sanfter Anstoß jeden Tag, nie ein schlechtes Gewissen.",
  },
  errors: {
    generic: "Etwas ist schiefgelaufen. Versuch es erneut.",
  },
};

const pt: typeof en = {
  title: "Férias, recuperação e exames",
  subtitle: "Distribui o trabalho em vez de o acumular no final",
  hub: {
    summer: "Férias de verão",
    summerDesc: "Organiza o teu verão sem o transformar num segundo ano letivo.",
    recovery: "Recuperação / disciplinas em atraso",
    recoveryDesc: "Um período reprovado, uma recuperação, um tema que nunca percebeste. O ano todo.",
    exam: "Preparação para exames",
    examDesc: "Exames escolares, exames universitários, certificados de línguas, provas de acesso.",
    active: "Os teus planos",
    empty: "Ainda não tens nenhum plano. Escolhe um acima e o Lumen constrói-o contigo.",
    open: "Abrir",
  },
  wizard: {
    summerIntro: "Vamos organizar o teu verão sem o transformar num segundo ano letivo.",
    debtsQ: "Tens disciplinas em atraso ou recuperações?",
    yes: "Sim",
    no: "Não",
    unknown: "Ainda não sei",
    debtsHint: "Vamos adicionar um plano de Recuperação junto ao teu plano de verão.",
    period: "O teu período de férias",
    lastDay: "Último dia de aulas",
    firstDay: "Primeiro dia do novo ano letivo",
    dailyMax: "Tempo máximo por dia",
    restDays: "Dias em que preferes não estudar",
    travel: "Viagens / ausências (opcional)",
    addTravel: "Adicionar uma viagem",
    from: "De",
    to: "Até",
    ageBand: "Nível escolar",
    primary: "Primário",
    middle: "Ensino básico",
    high: "Ensino secundário",
    university: "Universidade",
    subject: "Disciplina",
    subjectPh: "ex. Matemática",
    scopeQ: "O que precisas de recuperar?",
    scope: {
      topic: "Um tema",
      topics: "Vários temas",
      term1: "Primeiro período",
      term2: "Segundo período",
      year: "O ano todo",
      unknown: "Não sei",
    },
    unknownHint: "Sem problema: o Lumen vai perceber a partir do teu programa, testes anteriores e do material que enviares.",
    testDateQ: "Quando é o teu teste?",
    noTestDate: "Ainda não sei a data",
    noTestDateHint: "Começamos a trabalhar na mesma e atualizamos o plano assim que tiveres a data.",
    examName: "Para que te estás a preparar?",
    examNamePh: "ex. Análise 1, B2 First, prova de acesso",
    goal: "O teu objetivo (opcional)",
    goalPh: "ex. passar com uma nota sólida",
    create: "Criar o plano",
    creating: "A criar…",
    next: "Continuar",
    back: "Voltar",
  },
  items: {
    title: "O teu trabalho",
    add: "Adicionar trabalho",
    text: "Escreve",
    textPh: "Matemática: exercícios 1-120\nInglês: unidades 1-6\nLer O Barão nas Árvores\nResumo do capítulo 4",
    photo: "Foto / ficheiro",
    photoHint: "Fotografa a folha de trabalhos de casa: o Lumen reconhece disciplinas, páginas, exercícios e prazos.",
    parse: "Ler com IA",
    parsing: "A ler…",
    confirmTitle: "Verifica antes de guardar",
    confirmHint: "Corrige o que estiver errado e depois guarda.",
    save: "Guardar",
    empty: "Ainda não adicionaste nada.",
    left: "Faltam {{left}} de {{total}} {{unit}}",
    book: "Livro para ler",
    bookTitle: "Título",
    author: "Autor",
    pages: "Páginas",
    currentPage: "Página atual",
    finishBy: "Terminar até",
    finalTask: "Tarefa final",
    finalTasks: {
      none: "Nenhuma",
      summary: "Resumo",
      sheet: "Ficha de leitura",
      presentation: "Apresentação",
      report: "Relatório",
      questions: "Perguntas",
    },
    addBook: "Adicionar livro",
    perDay: "Cerca de {{n}} {{unit}} por dia é suficiente.",
    difficulty: "Dificuldade",
    remove: "Remover",
  },
  plan: {
    build: "Criar o meu plano",
    rebuild: "Refazer o plano",
    building: "O Lumen está a distribuir o trabalho…",
    summary: "O teu plano",
    phases: "Fases",
    today: "Hoje",
    todayEmpty: "Não há nada planeado para hoje. Aproveita.",
    totalToday: "Total de hoje: {{min}} min",
    done: "Feito",
    skip: "Saltar",
    start: "Começar",
    upcoming: "A seguir",
    behind: "Fiquei atrasado",
    behindHint: "O Lumen replaneia o que falta, com prioridades. Sem sermões.",
    reschedule: "Reorganizar o plano",
    rescheduleHint: "Falhaste um dia? O trabalho é distribuído por vários dias, nunca é tudo empurrado para amanhã.",
    ask: "Perguntar ao Lumen",
    askPh: "ex. Não fiz nada e já é 10 de agosto",
    send: "Enviar",
    noDays: "Define primeiro as datas do período.",
    backToSchool: "Volta às aulas",
    progress: "Progresso",
    readiness: "Preparação",
    daysLeft: "Faltam {{n}} dias",
    testOn: "Teste a {{date}}",
    onlyMinutes: "Só tenho {{n}} minutos",
    priority: { high: "Prioridade alta", medium: "Média", low: "Baixa" },
    status: { recovering: "Em recuperação", almost: "Quase pronto", ready: "Pronto", weak: "Precisa de reforço" },
  },
  study: {
    diagnostic: "Vamos ver como estás",
    diagnosticHint: "Um teste curto que não conta para a nota: serve para encontrar as tuas lacunas.",
    startDiagnostic: "Começar o teste",
    exercises: "Exercícios",
    exercisesHint: "Exercícios reais criados para ti, com correção completa.",
    generate: "Gerar exercícios",
    level: { base: "Base", intermediate: "Intermédio", advanced: "Avançado", exam: "Simulação de exame" },
    topicPh: "Tema (ex. equações do segundo grau)",
    yourAnswer: "A tua resposta",
    yourAnswerPh: "Escreve os teus passos, ou anexa uma foto do teu trabalho",
    correct: "Corrigir",
    correcting: "A corrigir…",
    verdict: { correct: "Correto", partly: "Quase", wrong: "Ainda não" },
    whereWrong: "Onde correu mal",
    why: "Porquê",
    howToFix: "Como corrigir",
    rule: "Regra a lembrar",
    similar: "Tentar um parecido",
    errorLog: "Os meus erros",
    errorLogEmpty: "Ainda não há erros registados.",
    times: "{{n}}x",
    simulate: "Simulado de exame",
    simulateHint: "Um teste real cronometrado. Soluções só no final.",
    duration: "Duração",
    custom: "Personalizado",
    startSim: "Começar o simulado",
    showSolutions: "Mostrar soluções",
    solution: "Solução",
    points: "{{n}} pts",
  },
  reminders: {
    title: "Lembrete diário",
    hint: "Um empurrãozinho todos os dias, nunca uma repreensão.",
  },
  errors: {
    generic: "Algo correu mal. Tenta novamente.",
  },
};

export const breaks: LangMap = { en, it, es, fr, de, pt };
